import { useState, useEffect, useMemo } from 'react';
import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Box } from '@mui/material';
import StandardTable from '../tables/StandardTable';

import { selectProject } from '../../features/project/projectSlice';
import { IRegistrant } from '../../types/registrant';
import { convertAllDates, capitalizeFirstLetter } from '../../utils/Functions';

const Calls = (props: ChildProps) => {
  const project = useSelector(selectProject);
  const { email } = props;
  const [calls, setCalls] = useState([]);
  const [registrant, setRegistrant] = useState<IRegistrant | null>(null);

  useQuery(GETREGISTRANT, {
    fetchPolicy: 'cache-and-network',
    variables: { filter: { project: project._id, email: email } },
    onCompleted: (data) => {
      setRegistrant(data.registrantOne);
    },
    onError: (err) => console.log(err),
  });

  const [getCalls, { loading }] = useLazyQuery(GETPHONEDETAILS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setCalls(data.callRailRegistrant.calls);
    },
    onError: (e) => {
      console.log(e);
    },
  });

  const [downloadAudio] = useLazyQuery(DOWNLOADAUDIO, {
    onCompleted: (data) => {
      const element = document.createElement('a');
      element.href = data.downloadAudio;
      element.target = '_blank';
      element.click();
    },
    onError: (e) => {
      console.log(e);
    },
  });

  useEffect(() => {
    if (registrant) {
      getCalls({ variables: { registrant: registrant._id, type: 'registrant' } });
    }
  }, [registrant]);

  const columns = useMemo(() => {
    return [
      {
        Header: 'Date',
        accessor: (rowData: any) => convertAllDates(rowData.start_time, 'PPpp'),
      },
      {
        Header: 'RDS User',
        accessor: (rowData: any) => (rowData.user ? rowData.user : rowData.business_phone_number ? rowData.business_phone_number : ''),
      },
      {
        Header: 'Answered',
        accessor: (rowData: any) => (rowData.answered ? <CheckIcon color="success" /> : <CloseIcon color="error" />),
      },
      {
        Header: 'Direction',
        accessor: (rowData: any) => capitalizeFirstLetter(rowData.direction),
      },
      {
        Header: 'Duration(s)',
        accessor: (rowData: any) => (rowData.duration ? rowData.duration : rowData.recording_duration),
      },
      {
        Header: 'Listen',
        accessor: (rowData: any) => {
          if (rowData.recording_player) {
            return (
              <Button variant="contained" color="primary" rel="noopener noreferrer" target="_blank" href={rowData.recording_player}>
                Listen
              </Button>
            );
          } else return;
        },
      },
      {
        Header: 'Download',
        accessor: (rowData: any) => {
          if (rowData.recording) {
            return (
              <Button variant="contained" color="primary" onClick={() => downloadAudio({ variables: { link: rowData.recording } })}>
                Download
              </Button>
            );
          } else return;
        },
      },
    ];
  }, [downloadAudio]);

  return (
    <Box>
      <StandardTable data={calls} columns={columns} loading={loading} />
    </Box>
  );
};

interface ChildProps {
  email: string;
  phone: string;
  type: string;
}

const GETREGISTRANT = gql`
  query registrantOne($filter: FilterFindOneRegistrantInput) {
    registrantOne(filter: $filter) {
      _id
      email
      firstName
      lastName
      primaryPhone
      postalCode
    }
  }
`;

const GETPHONEDETAILS = gql`
  query callRailRegistrant($registrant: MongoID, $dateStart: Date, $dateEnd: Date, $type: String!) {
    callRailRegistrant(registrant: $registrant, dateStart: $dateStart, dateEnd: $dateEnd, type: $type) {
      calls {
        answered
        customer_city
        direction
        recording
        recording_duration
        recording_player
        business_phone_number
        start_time
        voicemail
        duration
        user
      }
      texts {
        messages {
          direction
          content
          created_at
        }
        lastMessage
        phoneNumber
        name
      }
    }
  }
`;

const DOWNLOADAUDIO = gql`
  query downloadAudio($link: String!) {
    downloadAudio(link: $link)
  }
`;

export default Calls;
